import { BILLING_LIMITS, type BillingLimit } from '@aysar/contracts';

/** The declared levers one tier overrides. An absent lever inherits its base. */
export type TierLimitRow = Readonly<Partial<Record<BillingLimit, number>>>;

/** Tier key to its overrides. A tier absent from this map overrides nothing. */
export type TierLimitMap = Readonly<Record<string, TierLimitRow>>;

/**
 * The stored `billing.tierLimits` value, read one tier at a time.
 *
 * A lever the catalogue no longer declares is **dropped** rather than carried —
 * the exact opposite of the capability map beside it, and for a reason that
 * lives in the api: `billing.tierLimits`' value schema is a *declared* object
 * derived from `BILLING_LIMITS`, so the server already strips a retired lever on
 * parse. Carrying it here would be this screen re-adding something the api has
 * decided is gone. Nothing strips a capability, so there the write would destroy
 * it and it has to be carried.
 */
export function tierLimitMapOf(value: unknown): TierLimitMap {
  if (!isPlainObject(value)) {
    return {};
  }

  const map: Record<string, TierLimitRow> = {};

  for (const [tier, stored] of Object.entries(value)) {
    if (!isPlainObject(stored)) {
      continue;
    }

    const row: Partial<Record<BillingLimit, number>> = {};

    for (const lever of BILLING_LIMITS) {
      const candidate = stored[lever];

      if (typeof candidate === 'number' && Number.isInteger(candidate) && candidate >= 0) {
        row[lever] = candidate;
      }
    }

    map[tier] = row;
  }

  return map;
}

/**
 * One lever set, or cleared back to inheriting.
 *
 * `null` is the emptied box and is a **decision** — this tier has no number of
 * its own — where `0` is a deliberate quota of none. Collapsing the two would
 * make "stop overriding" unsayable, which is the same distinction the direct
 * price editor draws with `isEmptyNull`.
 */
export function withLimit(
  row: TierLimitRow | undefined,
  lever: BillingLimit,
  value: number | null,
): TierLimitRow {
  const next: Partial<Record<BillingLimit, number>> = { ...row };

  if (value === null) {
    delete next[lever];
  } else {
    next[lever] = value;
  }

  return next;
}

/**
 * The request body — **every** tier that still overrides something.
 *
 * Whole-map for the reason `toStoredCapabilities` spells out: the stored layer
 * is replaced, not merged into, so a tier omitted from the body silently stops
 * overriding. A tier whose last lever was cleared is omitted deliberately, since
 * an empty row and an absent row resolve identically and only one of them reads
 * as "overrides nothing" wherever the value is drawn.
 */
export function toStoredLimits(map: TierLimitMap): Record<string, Record<string, number>> {
  const body: Record<string, Record<string, number>> = {};

  for (const tier of Object.keys(map).sort()) {
    const row = map[tier];

    if (row !== undefined && Object.keys(row).length > 0) {
      body[tier] = { ...row };
    }
  }

  return body;
}

/** A JSON object, as opposed to an array, a null, or a primitive. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
